import { useState } from 'react'
import {
  Container,
  VStack,
  Box,
  Heading,
  Text,
  HStack,
  Badge,
  Spinner,
  Alert,
  Button,
  Field,
  InputGroup,
  Input,
  IconButton,
  CloseButton,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiPlus, FiCopy } from 'react-icons/fi'
import { useInvites, useRevokeInvite, useResendInvite } from '../hooks/useInvites'
import { IssueInviteModal } from '../components/admin/IssueInviteModal'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared'
import { useIsMobile } from '../hooks/useIsMobile'
import type { Invite, IssuedInvite } from '../types/invites'
import { publicOrigin } from '../lib/native/instance'

const statusBadgeColor: Record<string, string> = {
  Pending: 'yellow',
  Accepted: 'green',
  Revoked: 'gray',
  Expired: 'red',
}

const emailStatusBadgeColor: Record<string, string> = {
  Sent: 'green',
  Failed: 'red',
  NotConfigured: 'gray',
}

function AdminInvitesPage() {
  const { data: invites, isLoading, error } = useInvites()
  const revokeInvite = useRevokeInvite()
  const resendInvite = useResendInvite()
  const isMobile = useIsMobile()
  const [isIssueOpen, setIsIssueOpen] = useState(false)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [resent, setResent] = useState<IssuedInvite | null>(null)

  const handleRevoke = async (invite: Invite) => {
    setRevokingId(invite.id)
    try {
      await revokeInvite.mutateAsync(invite.id)
      toaster.create({ title: 'Invite revoked', type: 'success', duration: 3000 })
    } catch {
      toaster.create({ title: 'Failed to revoke invite', type: 'error', duration: 4000 })
    } finally {
      setRevokingId(null)
    }
  }

  const handleResend = async (invite: Invite) => {
    setResendingId(invite.id)
    try {
      const result = await resendInvite.mutateAsync(invite.id)
      setResent(result)
      toaster.create({ title: 'Invite resent', type: 'success', duration: 3000 })
    } catch {
      toaster.create({ title: 'Failed to resend invite', type: 'error', duration: 4000 })
    } finally {
      setResendingId(null)
    }
  }

  // [Native B1] The instance's origin; null on native with no instance → no link, copy disabled.
  const origin = publicOrigin()
  const resentAcceptUrl = resent && origin ? `${origin}${resent.acceptUrlPath}` : ''

  const copyResentLink = () => {
    navigator.clipboard.writeText(resentAcceptUrl)
    toaster.create({ type: 'success', title: 'Invite link copied' })
  }

  const columns: ResponsiveColumn<Invite>[] = [
    {
      header: 'Email',
      mobileRole: 'title',
      cell: (invite) => <Text fontWeight="medium">{invite.email}</Text>,
    },
    {
      header: 'Role',
      mobileRole: 'badge',
      cell: (invite) => <Badge bg="info.subtle" color="info.fg">{invite.role}</Badge>,
    },
    {
      header: 'Committee',
      mobileRole: 'meta',
      cell: (invite) => (
        <Text fontSize="sm" color={invite.committeeLabel ? 'gray.700' : 'gray.400'}>
          {invite.committeeLabel || 'Unassigned'}
        </Text>
      ),
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (invite) => (
        <Badge colorPalette={statusBadgeColor[invite.status] || 'gray'}>{invite.status}</Badge>
      ),
    },
    {
      header: 'Expires',
      mobileRole: 'meta',
      cell: (invite) => (
        <Text fontSize="sm" color="gray.500">
          {new Date(invite.expiresAt).toLocaleDateString()}
        </Text>
      ),
    },
    {
      header: 'Emailed',
      mobileRole: 'badge',
      cell: (invite) =>
        invite.emailStatus ? (
          <Badge colorPalette={emailStatusBadgeColor[invite.emailStatus] || 'gray'}>
            {invite.emailStatus}
          </Badge>
        ) : (
          <Text fontSize="sm" color="fg.muted">—</Text>
        ),
    },
    {
      header: 'Created',
      mobileRole: 'desktop-only',
      cell: (invite) => (
        <Text fontSize="sm" color="gray.500">
          {new Date(invite.createdAt).toLocaleDateString()}
        </Text>
      ),
    },
    {
      header: 'Actions',
      mobileRole: 'meta',
      cell: (invite) => {
        const canResend = invite.status === 'Pending' || invite.status === 'Expired'
        if (!canResend) {
          return <Text fontSize="sm" color="fg.muted">{'—'}</Text>
        }
        return (
          <HStack gap={2}>
            {invite.status === 'Pending' && (
              <Button
                size={isMobile ? 'sm' : 'xs'}
                minH={isMobile ? '44px' : undefined}
                variant="outline"
                colorPalette="red"
                loading={revokingId === invite.id}
                onClick={() => handleRevoke(invite)}
              >
                Revoke
              </Button>
            )}
            {canResend && (
              <Button
                size={isMobile ? 'sm' : 'xs'}
                minH={isMobile ? '44px' : undefined}
                variant="outline"
                colorPalette="brand"
                loading={resendingId === invite.id}
                onClick={() => handleResend(invite)}
              >
                Resend
              </Button>
            )}
          </HStack>
        )
      },
    },
  ]

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        <HStack justify="space-between" align="start">
          <Box>
            <Heading size="lg">Invites</Heading>
            <Text color="gray.500" mt={1}>
              Issue single-use invite links to bootstrap new accounts
            </Text>
          </Box>
          <Button colorPalette="brand" onClick={() => setIsIssueOpen(true)}><FiPlus />Invite user
                      </Button>
        </HStack>

        {resent && (
          <Alert.Root status={resent.emailStatus === 'Sent' ? 'success' : 'warning'} borderRadius="md">
            <Alert.Indicator />
            <VStack gap={2} align="stretch" flex={1}>
              <Text fontSize="sm">
                {resent.emailStatus === 'Sent'
                  ? `Invite resent — emailed to ${resent.email}.`
                  : 'Invite resent — email not sent, copy the link below.'}
              </Text>
              <Field.Root>
                <Field.Label fontSize="sm" color="fg.muted">New accept link</Field.Label>
                <InputGroup endElement={
                  <IconButton
                    aria-label="Copy invite link"
                    size="sm"
                    variant="ghost"
                    disabled={!resentAcceptUrl}
                    onClick={copyResentLink}><FiCopy /></IconButton>
                }>
                  <Input size="sm" value={resentAcceptUrl} readOnly />
                </InputGroup>
              </Field.Root>
            </VStack>
            <CloseButton size="sm" onClick={() => setResent(null)} />
          </Alert.Root>
        )}

        {isLoading && (
          <VStack py={12}>
            <Spinner size="lg" color="accent" />
            <Text color="gray.500">Loading invites...</Text>
          </VStack>
        )}

        {!!error && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            Failed to load invites. Please try again.
          </Alert.Root>
        )}

        {!isLoading && invites && (
          <ResponsiveTable
            columns={columns}
            rows={invites}
            keyFn={(invite) => invite.id}
            emptyMessage="No invites found."
          />
        )}
      </VStack>

      <IssueInviteModal isOpen={isIssueOpen} onClose={() => setIsIssueOpen(false)} />
    </Container>
  );
}

export default AdminInvitesPage
export { AdminInvitesPage }
